import { Component, OnInit, EventEmitter, Input, Output, HostListener } from '@angular/core';
import * as moment from 'moment';
import * as _ from 'lodash';
import { SharedService } from 'app/shared/services/shared.service';
import { DictionaryService } from 'app/shared/services/dictionary.service';
import { ReportsService } from 'app/shared/services/reports.service';

@Component({
  selector: 'datepicker-component',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
})
export class DatePickerComponent implements OnInit {
  public active: boolean;
  public showCustomRange: boolean;
  @Input() public startDate: Date;
  @Input() public endDate: Date;
  @Input() public disabled: boolean;
  @Input() public singleDate: boolean;
  @Input() public customTimezone: boolean;
  @Input() public reportType: string;
  @Input() public inputStyle: boolean;
  @Input() public emptyDateLabel: string;
  public leftDate: Date;
  public leftDateCurrDays: Array<any>;
  public leftDatePrevDays: Array<any>;
  public rightDate: Date;
  public rightDateCurrDays: Array<any>;
  public rightDatePrevDays: Array<any>;
  public startDateInput: string;
  public endDateInput: string;
  public predefinedDates: Array<any>;
  @Output() public startDateChanged: EventEmitter<Date>;
  @Output() public endDateChanged: EventEmitter<Date>;
  public subscriptions: Array<any>;

  constructor(
    public sharedService: SharedService,
    public reportsService: ReportsService,
    public dictionaryService: DictionaryService
  ) {
    const now: Date = this.reportsService.getCurrentDate(null, null, null, this.customTimezone);
    const startDate: Date = this.startDate ? this.startDate : now;
    const endDate: Date = this.endDate ? this.endDate : now;
    this.active = false;
    this.singleDate = false;
    this.showCustomRange = false;
    this.inputStyle = false;
    this.disabled = false;
    this.customTimezone = false;
    this.startDateChanged = new EventEmitter<Date>();
    this.endDateChanged = new EventEmitter<Date>();
    this.subscriptions = [];
    this.reportType = '';
    this.emptyDateLabel = '';
    this.initializePredefinedDates();
    this.selectDate(startDate, true);
    this.selectDate(endDate, true);
  }

  ngOnInit() {
    this.onDatesSelected();
    this.subscriptions.push(
      this.sharedService.getMessagesBus().subscribe((response: any) => {
        if (response.type === 'DATE_RANGE_CHANGE') {
          this.startDate = response.data.startDate;
          this.endDate = response.data.endDate;
        } else if (response.type === 'TIMEZONE_CHANGE') {
          this.initializePredefinedDates();
        } else if (response.type === 'CLOSE_SIBLING_DATEPICKERS') {
          this.closeDatePicker();
        }
      })
    );
  }

  ngOnChanges($event: any) {
    if (!this.active) {
      this.onDatesSelected();
    }
    if ($event.customTimezone) {
      this.initializePredefinedDates();
    }
  }

  initializePredefinedDates() {
    this.predefinedDates = [
      {
        title: 'TODAY',
        startDate: this.reportsService.getCurrentDate(null, null, null, this.customTimezone),
        endDate: this.reportsService.getCurrentDate(null, null, null, this.customTimezone),
      },
      {
        title: 'YESTERDAY',
        startDate: this.reportsService.getCurrentDate('subtract', 1, 'days', this.customTimezone),
        endDate: this.reportsService.getCurrentDate('subtract', 1, 'days', this.customTimezone),
      },
      {
        title: 'LAST_7_DAYS',
        startDate: this.reportsService.getCurrentDate('subtract', 6, 'days', this.customTimezone),
        endDate: this.reportsService.getCurrentDate(null, null, null, this.customTimezone),
      },
      {
        title: 'LAST_MONTH',
        startDate: this.reportsService.getCurrentDate('startLastMonth', null, null, this.customTimezone),
        endDate: this.reportsService.getCurrentDate('endLastMonth', null, null, this.customTimezone),
      },
      {
        title: 'MONTH_TO_DATE',
        startDate: this.reportsService.getCurrentDate('startThisMonth', null, null, this.customTimezone),
        endDate: this.reportsService.getCurrentDate(null, null, null, this.customTimezone),
      },
      {
        title: 'LAST_30_DAYS',
        startDate: this.reportsService.getCurrentDate('subtract', 30, 'days', this.customTimezone),
        endDate: this.reportsService.getCurrentDate(null, null, null, this.customTimezone),
      },
    ];
  }

  toggleDatePicker() {
    if (!this.active) { this.sharedService.sendMessage('CLOSE_SIBLING_DATEPICKERS'); }

    setTimeout(() => {
      this.active = !this.active;
      if (!this.active) {
        this.showCustomRange = false;
      }
    }, 200);
  }

  closeDatePicker() {
    this.active = false;
  }

  onDatesSelected() {
    const now: Date = this.reportsService.getCurrentDate(null, null, null, this.customTimezone);
    let startDate: Date;
    let endDate: Date;
    this.saveDatesToLocalStorage();

    if (this.startDate) {
      if (this.singleDate) { this.endDate = _.cloneDeep(this.startDate); }

      startDate = _.cloneDeep(this.startDate);
      endDate = this.singleDate ? _.cloneDeep(this.startDate) : _.cloneDeep(this.endDate);
      startDate.setDate(1);
      if (
        (startDate.getMonth() % 2 === 1 && endDate.getMonth() % 1 === 1) ||
        (startDate.getMonth() === now.getMonth() && startDate.getFullYear() === now.getFullYear())
      ) {
        if (!this.singleDate) { this.leftDate = this.getPrevMonth(startDate); }
        this.rightDate = startDate;
      } else {
        this.leftDate = startDate;
        this.rightDate = this.getNextMonth(startDate);
      }
      this.updateCalendar();
    }
  }

  updateCalendar(initialization: boolean = false) {
    const now: Date = new Date();
    let dateLimit: Date;

    const numOfDaysInLeftMonth: number = new Date(this.leftDate.getFullYear(), this.leftDate.getMonth() + 1, 0).getDate();
    const numOfDaysInRightMonth: number = new Date(
      this.rightDate.getFullYear(),
      this.rightDate.getMonth() + 1,
      0
    ).getDate();
    const firstDayOfLeftMonth: number = new Date(this.leftDate.getFullYear(), this.leftDate.getMonth(), 1).getDay();
    const firstDayOfRightMonth: number = new Date(this.rightDate.getFullYear(), this.rightDate.getMonth(), 1).getDay();
    const dummyObject: any = { title: '-' };
    let curDate: Date;
    let isDisabled = false;
    this.leftDatePrevDays = [];
    this.leftDateCurrDays = [];
    this.rightDatePrevDays = [];
    this.rightDateCurrDays = [];

    switch (this.reportType) {
      case 'cedatoLabs':
        dateLimit = new Date(now.getTime() - +this.reportsService.labsDaysLimit * 24 * 60 * 60 * 1000);
        break;
      case 'legacyAll':
        dateLimit = new Date(now.getTime() - +this.reportsService.legacyAllDaysLimit * 24 * 60 * 60 * 1000);
        break;
      case 'legacyBy':
        dateLimit = new Date(now.getTime() - +this.reportsService.legacyByDaysLimit * 24 * 60 * 60 * 1000);
        break;
      case 'campaign':
        dateLimit = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      default:
        dateLimit = new Date(now.getTime() - +this.reportsService.defaultDaysLimit * 24 * 60 * 60 * 1000);
    }

    for (let i = 0; i < firstDayOfLeftMonth; i++) {
      this.leftDatePrevDays.push(dummyObject);
    }

    for (let i = 0; i < numOfDaysInLeftMonth; i++) {
      curDate = new Date(this.leftDate.getFullYear(), this.leftDate.getMonth(), i + 1);
      isDisabled = dateLimit > curDate;

      this.leftDateCurrDays.push({
        title: i + 1,
        date: curDate,
        isDisabled,
      });
    }
    for (let i = 0; i < firstDayOfRightMonth; i++) {
      this.rightDatePrevDays.push(dummyObject);
    }
    for (let i = 0; i < numOfDaysInRightMonth; i++) {
      curDate = new Date(this.rightDate.getFullYear(), this.rightDate.getMonth(), i + 1);
      isDisabled = dateLimit > curDate;

      this.rightDateCurrDays.push({
        title: i + 1,
        date: curDate,
        isDisabled,
      });
    }
  }

  goToPrevMonth() {
    this.leftDate = this.getPrevMonth(this.leftDate);
    this.rightDate = this.getPrevMonth(this.rightDate);
    this.updateCalendar();
  }

  goToNextMonth() {
    this.leftDate = this.getNextMonth(this.leftDate);
    this.rightDate = this.getNextMonth(this.leftDate);
    this.updateCalendar();
  }

  getPrevMonth(date: any) {
    const prevDate: Date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (date.getMonth() === 0) {
      prevDate.setMonth(11);
      prevDate.setFullYear(prevDate.getFullYear() - 1);
    } else {
      prevDate.setMonth(prevDate.getMonth() - 1);
    }
    return prevDate;
  }

  getNextMonth(date: any) {
    const nextDate: Date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (date.getMonth() === 11) {
      nextDate.setMonth(0);
      nextDate.setFullYear(nextDate.getFullYear() + 1);
    } else {
      nextDate.setMonth(nextDate.getMonth() + 1);
    }
    return nextDate;
  }

  selectDate(date: any, initialization: boolean = false, startOrEnd: string = null) {
    let startOfDay: Date;
    let diffOfEndFromStart: number;
    if (date !== undefined) {
      if (!this.singleDate) {
        if (this.reportsService.isValidDate(date, this.customTimezone)) {
          if (startOrEnd === null) {
            if (this.endDate === null) {
              startOfDay = new Date(date.setHours(0, 0, 0, 0));
              diffOfEndFromStart = moment.duration(moment(startOfDay).diff(this.startDate)).asDays();
              if (diffOfEndFromStart < 0) {
                this.startDate = date;
                this.endDate = null;
              } else {
                this.endDate = date;
              }
            } else {
              this.startDate = date;
              this.endDate = null;
            }
            this.startDate = new Date(this.startDate.setHours(0, 0, 0, 0));
            this.endDate = this.endDate !== null ? new Date(this.endDate.setHours(23, 59, 59, 0)) : null;
          } else {
            if (startOrEnd === 'start') {
              this.startDate = date;
              if (moment.duration(moment(this.startDate).diff(this.endDate)).asDays() > 0) {
                this.endDate = date;
              }
            } else if (startOrEnd === 'end') {
              this.endDate = date;
            }
          }
          if (!this.active) {
            this.leftDate = this.startDate;
            this.rightDate = this.getNextMonth(this.leftDate);
          }
          if (!initialization) {
            this.startDateChanged.emit(this.startDate);
            this.endDateChanged.emit(this.endDate);
          }
          this.updateCalendar();
        } else {
          this.sharedService.showFlashMessage(this.dictionaryService.get('PLEASE_CHOOSE_A_VALID_DATE'), 'error');
        }
      } else {
        this.startDate = date;
        this.endDate = date;
        this.leftDate = this.startDate;
        this.updateCalendar(initialization);
        if (!initialization) {
          this.startDateChanged.emit(this.startDate);
        }
      }
    }
    this.saveDatesToLocalStorage();
  }

  onDateInput(date: string, startOrEnd: string) {
    if (moment(date, 'DD/MM/YYYY', false).isValid()) {
      if (startOrEnd === 'start') {
        this.selectDate(moment(date, 'DD/MM/YYYY', false).toDate(), false, startOrEnd);
      } else if (startOrEnd === 'end') {
        this.selectDate(moment(date, 'DD/MM/YYYY', false).toDate(), false, startOrEnd);
      }
    }
  }

  selectPredefinedDate(item: any) {
    this.selectDate(item.startDate);
    this.selectDate(item.endDate);
    this.toggleDatePicker();
  }

  isPredefinedSelected(item: any) {
    const isStart: boolean = this.startDate
      ? this.startDate.getDate() === item.startDate.getDate() &&
        this.startDate.getMonth() === item.startDate.getMonth() &&
        this.startDate.getFullYear() === item.startDate.getFullYear()
      : false;
    const isEnd: boolean = this.endDate
      ? this.endDate.getDate() === item.endDate.getDate() &&
        this.endDate.getMonth() === item.endDate.getMonth() &&
        this.endDate.getFullYear() === item.endDate.getFullYear()
      : false;
    return isStart && isEnd;
  }

  isRangeEdge(date: any) {
    const isStart: boolean = this.startDate
      ? this.startDate.getDate() === date.getDate() &&
        this.startDate.getMonth() === date.getMonth() &&
        this.startDate.getFullYear() === date.getFullYear()
      : false;
    const isEnd: boolean = this.endDate
      ? this.endDate.getDate() === date.getDate() &&
        this.endDate.getMonth() === date.getMonth() &&
        this.endDate.getFullYear() === date.getFullYear()
      : false;
    return isStart || isEnd;
  }

  isInRange(date: any) {
    let diffFromStart: number;
    let diffFromEnd: number;
    const currentDate: Date = _.cloneDeep(date);
    const tzDifference = Number(this.reportsService.getTimezoneOffset()) * 60 + date.getTimezoneOffset();
    currentDate.setTime(currentDate.getTime() + tzDifference * 60000);
    if (this.startDate !== null && this.endDate !== null) {
      diffFromStart = moment.duration(moment(this.startDate).diff(currentDate)).asDays();
      diffFromEnd = moment.duration(moment(this.endDate).diff(currentDate)).asDays();
      return diffFromStart < 1 && diffFromEnd >= 0;
    } else {
      return false;
    }
  }

  saveDatesToLocalStorage() {
    if (this.startDate && this.endDate) {
      const dates: any = {
        startDate: this.startDate,
        endDate: this.endDate,
      };
      localStorage.setItem('selectedDates', JSON.stringify(dates));
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize($event: any = null) {
    if (!this.sharedService.isMobile() || $event === 'orientationchange') {
      if (this.sharedService.layout !== 'small') {
        this.showCustomRange = false;
      }
    }
  }

  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange($event: any) {
    this.onResize('orientationchange');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick($event: any) {
    let clickedOutsideOfMainElement = true;
    const path: any =
      $event.path || ($event.composedPath && $event.composedPath()) || this.sharedService.composedPath($event.target);
    /*
      Close datepicker if needed
    */
    if (this.active) {
      for (const item in path) {
        if (path[item].id === 'datePicker') {
          clickedOutsideOfMainElement = false;
        }
      }
      if (clickedOutsideOfMainElement) {
        this.toggleDatePicker();
      }
    }
  }
}
